import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayerGroup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapComponent.css';

// Fix for Leaflet marker icons in webpack

// Store and hooks
import useRaceStore from '../../store/raceStore';
import useRaceData from '../../hooks/useRaceData';

// API fonksiyonları

// Record boat data (static reference data)
import recordBoatData from '../../data/recordBoatData';

/**
 * Custom boat icon component
 * Creates a triangle SVG icon with color and rotation based on boat type and heading
 */
const createBoatIcon = (boatType, cogDeg = 0) => {
  const config = {
    linea_rossa: {
      color: "#FF0000", // Kırmızı
      size: 32,
      strokeColor: "#FFFFFF", // Beyaz
      strokeWidth: 2
    },
    fenerbahce_dogus: {
      color: "#FFCC00", // Sarı
      size: 32, // Aynı boyut
      strokeColor: "#000066", // Lacivert
      strokeWidth: 2
    }
  };

  // Get configuration for the requested boat type
  const boatConfig = config[boatType];
  
  // SVG rotasyon açısı ayarları - bu kısım çok önemli
  let rotationAngle;
  
  if (boatType === 'fenerbahce_dogus') {
    // calculateHeading'den gelen açı (cogDeg) gerçek pusula açısıdır (0=Kuzey).
    // Kullanıcı geri bildirimine göre (rotationAngle=45 iken Kuzey'e bakıyor),
    // SVG'nin varsayılan yönü Kuzeybatı'dır (-45 derece).
    // Bu yüzden doğru yön için (cogDeg + 45) derece döndürmeliyiz.
    rotationAngle = (cogDeg + 45) % 360;
    console.log('FB Doğuş - Gerçek Açı (cogDeg):', cogDeg, 'SVG Rotasyon Açısı:', rotationAngle);
  } else {
    // Linea Rossa için doğrudan API'den gelen açıyı kullan
    rotationAngle = cogDeg;
  }
  
  // Create SVG triangle icon with rotation based on Course Over Ground
  const svgIcon = L.divIcon({
    html: `
      <svg width="${boatConfig.size}" height="${boatConfig.size}" viewBox="0 0 24 24" 
           style="transform: rotate(${rotationAngle}deg)">
           <!-- Tüm tekneler için doğrudan hesaplanan rotasyon açısını kullanıyoruz, offset yok -->
        <path d="M12 2 L20 20 L12 16 L4 20 Z" 
              fill="${boatConfig.color}" 
              stroke="${boatConfig.strokeColor}" 
              stroke-width="${boatConfig.strokeWidth}"/>
      </svg>
    `,
    className: `boat-icon ${boatType}`,
    iconSize: [boatConfig.size, boatConfig.size],
    iconAnchor: [boatConfig.size/2, boatConfig.size/2]
  });

  return svgIcon;
};

/**
 * Creates checkpoint marker icons
 */
const createCheckpointIcon = (type) => {
  const colors = {
    start: "#4CAF50", // Green
    checkpoint: "#FFD700", // Yellow
    finish: "#F44336" // Red
  };

  return L.divIcon({
    html: `
      <div style="background-color: ${colors[type]}; width: 12px; height: 12px; border-radius: 50%; 
                  border: 2px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.4);"></div>
    `,
    className: `checkpoint-marker checkpoint-${type}`,
    iconSize: [16, 16],
    iconAnchor: [8, 8]
  });
};

/**
 * Auto-view map component
 * Keeps both boats in view by adjusting the map bounds
 * Modified to allow manual zoom control
 */
const AutoViewBothBoats = ({ position1, position2 }) => {
  const map = useMap();
  const [userInteracted, setUserInteracted] = useState(false);
  const [lastAutoZoom, setLastAutoZoom] = useState(Date.now());
  const [initialZoomDone, setInitialZoomDone] = useState(false);
  
  // İlk yükleme için hemen çalışan effect - daha hızlı merkezleme için
  useEffect(() => {
    // Eğer harita yüklendiyse ve konum varsa hemen ilk merkezlemeyi yap
    if (!initialZoomDone && position1) {
      if (position2) {
        // Her iki tekne de varsa sınırları ayarla
        const bounds = L.latLngBounds([position1, position2]);
        map.fitBounds(bounds, {
          animate: false, // İlk yüklemede animasyon yok - daha hızlı
          maxZoom: 9
        });
      } else {
        // Sadece Linea Rossa varsa onu merkeze al
        map.setView(position1, 9, { animate: false });
      }
      
      console.log('İlk merkezleme yapıldı:', position1);
      setInitialZoomDone(true);
      setLastAutoZoom(Date.now());
    }
  }, [map, position1, position2, initialZoomDone]);
  
  // İlk yükleme ve 60 saniyelik reset için effect
  useEffect(() => {
    // Kullanıcı etkileşimlerini izle
    const detectUserInteraction = () => {
      setUserInteracted(true);
      // Kullanıcı etkileşimden 60 saniye sonra otomatik görünüme dönmeyi etkinleştir
      setTimeout(() => {
        setUserInteracted(false);
      }, 60000); // 60 saniye sonra otomatik görünüme dönmeyi sağla
    };
    
    // Harita etkileşimlerini dinle
    map.on('zoom', detectUserInteraction);
    map.on('drag', detectUserInteraction);
    
    // Temizleme işlevi
    return () => {
      map.off('zoom', detectUserInteraction);
      map.off('drag', detectUserInteraction);
    };
  }, [map]);
  
  // Otomatik görünüm ayarlama
  useEffect(() => {
    // İlk merkezleme yapılmadıysa bekle
    if (!initialZoomDone) return;
    
    // Eğer kullanıcı harita ile etkileşimdeyse, otomatik ayarlamayı atla
    if (userInteracted) {
      console.log('Kullanıcı harita ile etkileşimde, otomatik görünüm atlanıyor');
      return;
    }
    
    // Son otomatik zoom'dan bu yana en az 5 saniye geçti mi?
    const now = Date.now();
    if (now - lastAutoZoom < 5000) {
      return; // 5 saniyeden daha sık otomatik zoom yapma
    }
    
    if (position1 && position2) {
      // İki tekne arasındaki mesafeyi hesapla (km cinsinden)
      const distance = map.distance(position1, position2) / 1000;
      
      // Mesafeye göre uygun zoom seviyesi belirle
      // Daha uzak mesafeler için daha düşük zoom seviyesi
      let zoomLevel = 10; // Varsayılan zoom seviyesi
      
      if (distance > 500) {
        zoomLevel = 6;
      } else if (distance > 300) {
        zoomLevel = 7;
      } else if (distance > 150) {
        zoomLevel = 8;
      } else if (distance > 50) {
        zoomLevel = 9;
      }
      
      // Create bounds that include both boats
      const bounds = L.latLngBounds([position1, position2]);
      
      // Sınırları genişlet (% olarak)
      const extendPercentage = 0.3; // %30 daha geniş görünüm
      
      // Sınırları genişlet
      const northWest = bounds.getNorthWest();
      const southEast = bounds.getSouthEast();
      
      const latDiff = Math.abs(northWest.lat - southEast.lat) * extendPercentage;
      const lngDiff = Math.abs(northWest.lng - southEast.lng) * extendPercentage;
      
      const extendedBounds = L.latLngBounds(
        [northWest.lat + latDiff, northWest.lng - lngDiff],
        [southEast.lat - latDiff, southEast.lng + lngDiff]
      );
      
      // Fit the map to the extended bounds
      map.fitBounds(extendedBounds, {
        animate: true,
        duration: 0.5,
        maxZoom: zoomLevel
      });
      
      // Son otomatik zoom zamanını güncelle
      setLastAutoZoom(now);
      
      console.log('Harita görünümü her iki tekneyi içerecek şekilde ayarlandı. Mesafe:', distance.toFixed(1), 'km, Zoom:', zoomLevel);
    } else if (position1 && !userInteracted) {
      // Eğer sadece Linea Rossa konumu varsa, onu merkeze al ve yakınlaştır
      map.setView(position1, 9, {
        animate: true,
        duration: 0.5
      });
      
      // Son otomatik zoom zamanını güncelle
      setLastAutoZoom(now);
      console.log('Harita Linea Rossa teknesine odaklandı:', position1);
    }
  }, [map, position1, position2, userInteracted, lastAutoZoom, initialZoomDone]);
  
  return null;
};

// Burada calculateFBDogusPosition fonksiyonu tanımını kaldırıyorum ve aşağıda yeniden tanımlayacağım

/**
 * Main Map Component
 * Displays the race map with boats, checkpoints and route lines
 */
const MapComponent = () => {
  // API'den doğrudan veri almak için state'ler
  const [apiData, setApiData] = useState(null);
  
  // Bağlantı hatası durumu için state
  const [apiError, setApiError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Ortam kontrolü - Vercel'de çalışıyorsa veya HTTPS ise proxy kullan
  const isVercelEnvironment = typeof window !== 'undefined' && window.location.hostname.includes('vercel.app');
  const isHttpsEnvironment = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const isProduction = isVercelEnvironment || isHttpsEnvironment;
  
  // Doğrudan API'den güncel veri al
  useEffect(() => {
    const getLatestData = async () => {
      try {
        // API endpoint'i - üretim ortamında proxy kullan, geliştirme ortamında doğrudan API'ye bağlan
        const apiUrl = isProduction
          ? `/api/race?t=${Date.now()}`
          : `http://mavivatan.imarine.com.tr:3583/api/v1/race?t=${Date.now()}`;
        
        console.log(`MapComponent: API isteği yapılıyor: ${apiUrl}`);
        
        // API'den taze veri al
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            // 5 saniye timeout ekle
            signal: AbortSignal.timeout(5000) 
          }
        );
        
        if (!response.ok) {
          throw new Error(`API isteği başarısız: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('MapComponent: Doğrudan API yanıtı:', data);
        
        if (!data || !data.race || !data.tracks || !data.tracks.length) {
          throw new Error('API yanıtı geçerli veri içermiyor');
        }
        
        // API verisini state'e kaydet
        setApiData(data);
        setApiError(false); // Hata durumunu sıfırla
        
        // Harita merkezini hemen güncelle - veri geldiğinde hemen haritayı güncelle
        if (data.tracks && data.tracks.length > 0 && data.tracks[0].lat && data.tracks[0].lon) {
          console.log('Harita merkezi hemen güncellenecek: ', [data.tracks[0].lat, data.tracks[0].lon]);
        }
      } catch (err) {
        console.error('MapComponent: API veri alma hatası:', err);
        
        // Hata durumunda mesajı göster
        console.log('API bağlantı hatası:', err.message);
        setApiError(true);
        setErrorMessage(err.message);
        
        // Son başarılı veriyi koru (apiData null değilse)
      }
    };
    
    // İlk yüklemede veri al - öncelikli olarak ve hemen çağrılıyor
    getLatestData();
    
    // Her 15 saniyede bir güncelle
    const intervalId = setInterval(getLatestData, 15000);
    
    return () => clearInterval(intervalId);
  }, [isProduction]); // isProduction bağımlılığını ekledik
  
  // Ayrıca store'dan gelen veriyi de kullan (yedek olarak)
  const raceData = useRaceData();
  
  // Marker referansları
  const currentBoatMarkerRef = useRef(null);
  const fbDogusMarkerRef = useRef(null);
  
  // Get checkpoints and prepare map data
  const checkpoints = recordBoatData.checkpoints;
  
  // Fenerbahçe Doğuş için etap bilgilerini recordBoatData'dan al
  const fbDogusStages = recordBoatData.stages.map(stage => {
    return {
      start: [stage.start_coords.lat, stage.start_coords.lng],
      end: [stage.end_coords.lat, stage.end_coords.lng],
      distance: stage.distance_nm,
      time: stage.elapsed_time_hours,
      vmg: stage.vmg,
      from: stage.from,
      to: stage.to
    };
  });
  
  // Fenerbahçe Doğuş için deniz rotası - useMemo ile optimize edildi
  // Fenerbahçe rotası (şu anda kullanılmıyor, ancak referans olarak tutuyoruz)
  /* const fenerbahceRoute = [React.useMemo(() => {
    // Ana rota noktalarını döndür
    return [
      // Hopa - Sinop arası (Karadeniz kıyısı boyunca)
      [41.42283, 41.42973], // Hopa
      [41.45000, 41.35000], // Hopa çıkışı
      [41.48000, 41.20000], // Karadeniz açıkları
      [41.52000, 41.00000], // Karadeniz açıkları
      [41.60000, 40.70000], // Karadeniz açıkları
      [41.70000, 40.30000], // Karadeniz açıkları
      [41.80000, 39.90000], // Karadeniz açıkları
      [41.90000, 39.50000], // Karadeniz açıkları
      [42.00000, 39.00000], // Karadeniz açıkları
      [42.10000, 38.50000], // Karadeniz açıkları
      [42.20000, 38.00000], // Karadeniz açıkları
      [42.25000, 37.50000], // Karadeniz açıkları
      [42.30000, 37.00000], // Karadeniz açıkları
      [42.32000, 36.50000], // Karadeniz açıkları
      [42.30000, 36.00000], // Karadeniz açıkları
      [42.25000, 35.50000], // Karadeniz açıkları
      [42.18000, 35.10000], // Sinop yaklaşımı
      [42.11667, 34.94473], // Sinop
      [42.05000, 34.80000], // Sinop çıkışı
      [42.00000, 34.50000], // Karadeniz açıkları
      [41.95000, 34.20000], // Karadeniz açıkları
      [41.90000, 33.90000], // Karadeniz açıkları
      [41.85000, 33.60000], // Karadeniz açıkları
      [41.80000, 33.30000], // Karadeniz açıkları
      [41.75000, 33.00000], // Karadeniz açıkları
      [41.70000, 32.70000], // Karadeniz açıkları
      [41.65000, 32.40000], // Karadeniz açıkları
      [41.60000, 32.10000], // Karadeniz açıkları
      [41.55000, 31.80000], // Karadeniz açıkları
      [41.50000, 31.50000], // Karadeniz açıkları
      [41.45000, 31.20000], // Karadeniz açıkları
      [41.40000, 30.90000], // Karadeniz açıkları
      [41.35000, 30.60000], // Karadeniz açıkları
      [41.30000, 30.30000], // Karadeniz açıkları
      [41.25000, 30.00000], // Karadeniz açıkları
      [41.23000, 29.70000], // Karadeniz açıkları
      [41.22000, 29.40000], // Boğaz girişine yaklaşım
      [41.22897, 29.11507]  // İstanbul Boğazı girişi
    ];
  }, []);  // Boş dependency array ile sadece bir kez oluşturulur
  
  // Boğaz geçişi ve diğer noktalar (kullanılmıyor)
  // Not: Aşağıdaki rota noktaları artık kullanılmıyor, ancak referans için burada yorum olarak tutuluyor
  /* 
    Marmara Denizi geçişi (Tamamen denizden):
    [40.75000, 28.50000], // Marmara Denizi
    [40.70000, 28.25000], // Marmara Denizi
    [40.65000, 28.00000], // Marmara Denizi
    [40.60000, 27.75000], // Marmara Denizi
    [40.55000, 27.50000], // Marmara Denizi
    [40.50000, 27.25000], // Marmara Denizi
    [40.45000, 27.00000], // Marmara Denizi
    [40.40000, 26.75000], // Marmara Denizi
    [40.35000, 26.50000], // Marmara Denizi
    [40.30000, 26.40000], // Çanakkale Boğazı girişi
  */
  
  /* Çanakkale Boğazı geçişi ve diğer noktalar
    // Çanakkale Boğazı geçişi (Tamamen denizden)
    [40.28000, 26.38000], // Boğaz girişi
    [40.26000, 26.36000], // Boğaz içi
    [40.24000, 26.34000], // Boğaz içi
    [40.22000, 26.32000], // Boğaz içi
    [40.20000, 26.30000], // Boğaz içi
    [40.18000, 26.28000], // Boğaz içi
    [40.16000, 26.26000], // Boğaz içi
    [40.14000, 26.24000], // Boğaz içi
    [40.12000, 26.22000], // Boğaz içi
    [40.10000, 26.20000], // Boğaz içi
    [40.08000, 26.19500], // Boğaz içi
    [40.06000, 26.19300], // Boğaz içi
    [40.04150, 26.19118], // Çanakkale Boğazı çıkışı
  */
  
  /* Ege Denizi ve diğer noktalar
    // Ege Denizi geçişi (Tamamen denizden)
    [40.00000, 26.18000], // Ege Denizi girişi
    [39.90000, 26.15000], // Ege Denizi
    [39.80000, 26.12000], // Ege Denizi
    [39.70000, 26.09000], // Ege Denizi
    [39.60000, 26.06000], // Ege Denizi
    [39.50000, 26.03000], // Ege Denizi
    [39.40000, 26.00000], // Ege Denizi
    [39.30000, 25.97000], // Ege Denizi
    [39.20000, 25.94000], // Ege Denizi
    [39.10000, 25.91000], // Ege Denizi
    [39.00000, 25.88000], // Ege Denizi
  */
  
  /* Ege Denizi devam
    [38.90000, 25.85000], // Ege Denizi
    [38.80000, 25.82000], // Ege Denizi
    [38.70000, 25.79000], // Ege Denizi
    [38.60000, 25.76000], // Ege Denizi
    [38.50000, 25.80000], // Ege Denizi
    [38.40000, 25.85000], // Ege Denizi
    [38.30000, 25.90000], // Ege Denizi
    [38.20000, 25.95000], // Ege Denizi
    [38.10000, 26.00000], // Ege Denizi
    [38.00000, 26.05000], // Ege Denizi
    [37.90000, 26.10000], // Ege Denizi
    [37.80000, 26.15000], // Ege Denizi
    [37.70000, 26.20000], // Ege Denizi
  */
  
  /* Ege Denizi ve Knidos yaklaşımı
    [37.60000, 26.25000], // Ege Denizi
    [37.50000, 26.30000], // Ege Denizi
    [37.40000, 26.35000], // Ege Denizi
    [37.30000, 26.40000], // Ege Denizi
    [37.20000, 26.45000], // Ege Denizi
    [37.10000, 26.50000], // Ege Denizi
    [37.00000, 26.60000], // Ege Denizi
    [36.90000, 26.70000], // Ege Denizi
    [36.80000, 26.90000], // Ege Denizi
    [36.75000, 27.10000], // Ege Denizi
    [36.70000, 27.30000], // Knidos yaklaşımı
    [36.68742, 27.35670], // Knidos Geçişi
  */
  
  /* Akdeniz geçişi (Knidos - Kıbrıs) - Tamamen denizden
    [36.65000, 27.40000], // Knidos çıkışı
    [36.60000, 27.60000], // Akdeniz
    [36.55000, 27.80000], // Akdeniz
    [36.50000, 28.00000], // Akdeniz
    [36.45000, 28.20000], // Akdeniz
    [36.40000, 28.40000], // Akdeniz
    [36.35000, 28.60000], // Akdeniz
    [36.30000, 28.80000], // Akdeniz
    [36.25000, 29.00000], // Akdeniz
    [36.20000, 29.20000], // Akdeniz
  */
  
  /* Akdeniz devam
    [36.15000, 29.40000], // Akdeniz
    [36.10000, 29.60000], // Akdeniz
    [36.05000, 29.80000], // Akdeniz
    [36.00000, 30.00000], // Akdeniz
    [35.95000, 30.20000], // Akdeniz
    [35.90000, 30.40000], // Akdeniz
    [35.85000, 30.60000], // Akdeniz
    [35.80000, 30.80000], // Akdeniz
    [35.75000, 31.00000], // Akdeniz
    [35.70000, 31.20000], // Akdeniz
    [35.65000, 31.40000], // Akdeniz
    [35.60000, 31.60000], // Akdeniz
    [35.55000, 31.80000], // Akdeniz
    [35.50000, 32.00000], // Akdeniz
  */
  
  /* Akdeniz devam ve Kıbrıs yaklaşımı
    [35.45000, 32.20000], // Akdeniz
    [35.40000, 32.40000], // Akdeniz
    [35.35000, 32.60000], // Akdeniz
    [35.30000, 32.80000], // Akdeniz
    [35.25000, 33.00000], // Akdeniz
    [35.20000, 33.20000], // Akdeniz
    [35.25000, 33.40000], // Akdeniz
    [35.30000, 33.60000], // Akdeniz
    [35.35000, 33.80000], // Akdeniz
    [35.40000, 34.00000], // Akdeniz
    [35.45000, 34.20000], // Akdeniz
    [35.50000, 34.40000], // Akdeniz
  */
  
  /* Kıbrıs ve İskenderun geçişi
    [35.72417, 34.60432], // Kıbrıs
    
    // Kıbrıs - İskenderun (Tamamen denizden)
    [35.75000, 34.70000], // Kıbrıs çıkışı
    [35.80000, 34.80000], // Akdeniz
    [35.85000, 34.90000], // Akdeniz
    [35.90000, 35.00000], // Akdeniz
    [35.95000, 35.10000], // Akdeniz
    [36.00000, 35.20000], // Akdeniz
    [36.05000, 35.30000], // Akdeniz
    [36.10000, 35.40000], // Akdeniz
    [36.15000, 35.50000], // Akdeniz
  */
  
  /* İskenderun yaklaşımı ve varış
    [36.20000, 35.60000], // Akdeniz
    [36.25000, 35.70000], // Akdeniz
    [36.30000, 35.80000], // Akdeniz
    [36.35000, 35.90000], // Akdeniz
    [36.40000, 36.00000], // Akdeniz
    [36.35000, 36.05000], // İskenderun yaklaşımı
    [36.25000, 36.10000], // İskenderun yaklaşımı
    [36.18000, 36.15000], // İskenderun yaklaşımı
    [36.12917, 36.17900], // İskenderun
  */
  
  // Rota bilgileri burada sona eriyor
  // Not: Rota noktaları artık kullanılmıyor
  
  // Fenerbahçe Doğuş rota bilgisi
  // Not: Rota çizgisi şu anda kullanılmıyor
  
  // Etap başlangıç ve bitiş noktalarını haritada göstermek için
  const stagePoints = fbDogusStages && fbDogusStages.length > 0 ? 
    fbDogusStages.map(stage => stage.start).concat([fbDogusStages[fbDogusStages.length - 1].end]) : 
    [];
  
  // Doğrudan API'den gelen veriyi veya store'dan gelen veriyi kullan
  const currentData = apiData || (raceData.currentBoat ? { race: raceData.raceInfo, tracks: [raceData.currentBoat] } : null);
  
  // API'den gelen güncel veriyi kullan - güvenli şekilde
  let currentBoatPosition = null;
  let currentBoatCOG = 0;
  let lineaRossaBoat = null; // Anlık hız için tekne nesnesini sakla
  
  // Initial map center - Linea Rossa'nın güncel konumu veya varsayılan başlangıç noktası
  const startPoint = currentBoatPosition || [checkpoints[0].coordinates.lat, checkpoints[0].coordinates.lng];
  
  if (currentData && currentData.tracks && currentData.tracks.length > 0) {
    const track = currentData.tracks[0];
    lineaRossaBoat = track; // Tekne nesnesini sakla
    
    // Güvenli şekilde değerleri al
    if (track.lat !== undefined && track.lon !== undefined) {
      currentBoatPosition = [track.lat, track.lon];
    }
    currentBoatCOG = track.cog_deg !== undefined ? track.cog_deg : 0;
    
    // Konum bilgisini konsola yazdır (hata ayıklama için)
    console.log('Harita: Güncel tekne konumu:', currentBoatPosition);
    console.log('Harita: Güncel tekne anlık hız:', track.sog_kn);
  }
  
  // State for FB Doğuş position - başlangıçta Hopa'da
  const [fbDogusPosition, setFbDogusPosition] = useState([41.42283, 41.42973]);
  
  // FB Doğuş teknesi için rotasyon açısı (hedefe doğru bakması için)
  const [fbDogusHeading, setFbDogusHeading] = useState(270);

  // Etap mesafeleri ve VMG değerleri - tabloya göre (useMemo ile sabitlendi)
  const etapData = useMemo(() => [
    { from: "hopa", to: "sinop", distance: 295, time: 61, vmg: 4.84 },            // Etap 1
    { from: "sinop", to: "bosphorusEntry", distance: 265, time: 54, vmg: 4.91 },   // Etap 2
    { from: "bosphorusEntry", to: "canakkaleExit", distance: 165, time: 19, vmg: 8.68 }, // Etap 3
    { from: "canakkaleExit", to: "knidos", distance: 220, time: 33, vmg: 6.67 },    // Etap 4
    { from: "knidos", to: "cyprus", distance: 360, time: 53, vmg: 6.79 },          // Etap 5
    { from: "cyprus", to: "iskenderun", distance: 95, time: 22, vmg: 4.32 }         // Etap 6
  ], []);  // Boş bağımlılık dizisi, değerlerin sabit olduğunu belirtir
  
  // FB Doğuş teknesi için etap konumları - Zaman geçtikçe ilerleyen konumlandırma
  useEffect(() => {
    // Tüm etaplara ait koordinatları tanımla - kullanıcının verdiği tablo referans alındı
    const checkpointCoordinates = {
      hopa: [41.42283, 41.42973],                // Start - Hopa Limanı (41°25.370 N / 41°25.784 E)
      sinop: [42.11667, 34.94473],               // CP1 - Sinop (42°07.000 N / 34°56.684 E)
      bosphorusEntry: [41.22897, 29.11507],      // CP2 - Boğaz Girişi (41°13.738 N / 29°06.904 E)
      canakkaleExit: [40.04150, 26.19118],      // CP3 - Çanakkale Boğazı Çıkışı (40°02.490 N / 26°11.471 E)
      knidos: [36.68742, 27.35670],              // CP4 - Knidos Geçişi (36°41.245 N / 27°21.402 E)
      cyprus: [35.72417, 34.60432],              // CP5 - Kıbrıs (35°43.450 N / 34°36.259 E)
      iskenderun: [36.12917, 36.17900]           // Finish - İskenderun (36°07.750 N / 36°10.740 E)
    };
    
    // Etap 2 için rota noktaları - Sinop > Boğaz Girişi
    const routes = {
      etap2: [
        checkpointCoordinates.sinop,              // Sinop (başlangıç)
        [42.10000, 34.50000],                    // Sinop çıkışı
        [42.09000, 34.00000],                    // Karadeniz açıkları
        [42.08500, 33.50000],                    // Karadeniz açıkları
        [42.08000, 33.00000],                    // Karadeniz açıkları
        [42.07800, 32.50000],                    // Karadeniz açıkları
        [42.07700, 32.00000],                    // Karadeniz açıkları
        [42.07650, 31.50000],                    // Karadeniz açıkları
        [42.075583, 30.991694],                  // Ara nokta
        [41.90000, 30.80000],                    // Karadeniz açıkları
        [41.80000, 30.60000],                    // Karadeniz açıkları
        [41.70000, 30.40000],                    // Karadeniz açıkları
        [41.60000, 30.20000],                    // Karadeniz açıkları
        [41.50000, 30.00000],                    // Karadeniz açıkları
        [41.40000, 29.80000],                    // Karadeniz açıkları
        [41.30000, 29.50000],                    // Boğaz girişine yaklaşım
        checkpointCoordinates.bosphorusEntry     // İstanbul Boğazı girişi (bitiş)
      ],
      
      // Etap 3 için rota noktaları - Boğaz Girişi > Çanakkale Çıkışı
      // Kullanıcının verdiği gerçek rota noktaları (DD° MM.mmm -> decimal derece formatında)
      etap3: [
        checkpointCoordinates.bosphorusEntry,     // Boğaz Girişi (başlangıç)
        [41.22018, 29.14308],                    // 1. 041° 13.211N, 029° 08.585E 
        [41.13582, 29.07052],                    // 2. 041° 08.149N, 029° 04.231E
        [41.04583, 29.02958],                    // 3. 041° 02.750N, 029° 01.775E
        [40.96398, 28.93652],                    // 4. 040° 57.839N, 028° 56.191E
        [40.81862, 27.75683],                    // 5. 040° 49.117N 027° 45.410E
        [40.72663, 27.37783],                    // 6. 040° 43.598N 027° 22.670E
        [40.5444, 27.04435],                     // 7. 040° 32.664N 027° 02.661E
        [40.41717, 26.70208],                    // 8. 040° 25.030N 026° 42.125E
        [40.29913, 26.57017],                    // 9. 040° 17.948N 026° 34.210E
        [40.19827, 26.39532],                    // 10. 040° 11.896N 026° 23.719E
        [40.07538, 26.28292],                    // 11. 040° 04.523N 026° 16.975E
        [39.9659, 26.14943],                     // 12. 039° 57.954N, 026° 08.966E
        checkpointCoordinates.canakkaleExit       // Çanakkale Boğazı Çıkışı (bitiş)
      ],
      
      // Etap 4 için rota noktaları - Çanakkale Çıkışı > Knidos
      etap4: [
        checkpointCoordinates.canakkaleExit,       // Çanakkale Çıkışı (başlangıç)
        [39.80000, 26.10000],                    // Ege Denizi
        [39.50000, 26.20000],                    // Ege Denizi
        [39.20000, 26.30000],                    // Ege Denizi
        [38.90000, 26.40000],                    // Ege Denizi
        [38.60000, 26.50000],                    // Ege Denizi
        [38.30000, 26.60000],                    // Ege Denizi
        [38.00000, 26.70000],                    // Ege Denizi
        [37.70000, 26.80000],                    // Ege Denizi
        [37.40000, 26.90000],                    // Ege Denizi
        [37.10000, 27.00000],                    // Ege Denizi
        [36.90000, 27.20000],                    // Knidos yaklaşımı
        checkpointCoordinates.knidos             // Knidos (bitiş)
      ]
    };
    
    // Rota hesaplama fonksiyonu
    const updateBoatPosition = () => {
      // 1. Başlangıçtan (22 Mayıs 05:46) şu ana kadar geçen toplam süreyi hesapla
      const startDate = new Date(2025, 4, 22, 5, 46, 0); // Ay indeksi 0'dan başlar (4 = Mayıs)
      const currentDate = new Date(); // Güncel tarih ve saat
      
      // Toplam geçen süreyi saate çevir
      const totalElapsedHours = (currentDate - startDate) / (1000 * 60 * 60);
      
      console.log('\n==== FB DOĞUŞ POZİSYON HESAPLAMA ====');
      console.log('Başlangıç tarihi:', startDate.toISOString());
      console.log('Güncel tarih:', currentDate.toISOString());
      console.log('Toplam geçen süre:', totalElapsedHours.toFixed(2), 'saat');
      
      // Etap sürelerini ve bişikimli süreleri hesapla - etapData'dan
      const etapDurations = etapData.map(etap => etap.time);
      const cumulativeDurations = etapDurations.reduce((acc, duration, index) => {
        if (index === 0) return [duration];
        return [...acc, acc[index - 1] + duration];
      }, []);
      
      console.log('Etap süreleri:', etapDurations);
      console.log('Bişikimli süreler:', cumulativeDurations);
      
      // Hangi etapta olduğunu belirle
      let currentStage = 0;
      let stageElapsedHours = 0;
      
      for (let i = 0; i < cumulativeDurations.length; i++) {
        if (totalElapsedHours <= cumulativeDurations[i]) {
          currentStage = i;
          stageElapsedHours = i === 0 ? totalElapsedHours : totalElapsedHours - cumulativeDurations[i-1];
          break;
        }
        currentStage = i + 1; // Tüm etaplar tamamlanmışsa, son etap
      }
      
      // Eğer tüm etaplar tamamlanmışsa son etapın sonuna koy
      if (currentStage >= etapData.length) {
        currentStage = etapData.length - 1;
        stageElapsedHours = etapDurations[currentStage]; // Etapın tam süresi
      }
      
      console.log('Güncel etap:', currentStage + 1); // 1-based index göster
      console.log('Etaptaki geçen süre:', stageElapsedHours.toFixed(2), 'saat');
      
      // Etap bilgilerini al
      const currentEtap = etapData[currentStage];
      const etapTotalTime = currentEtap.time;
      const etapDistance = currentEtap.distance;
      const etapVMG = currentEtap.vmg;
      
      console.log('Etap bilgileri:', 
                 'Mesafe:', etapDistance, 'NM', 
                 'Süre:', etapTotalTime, 'saat', 
                 'VMG:', etapVMG, 'knot');
      
      // Etaptaki ilerleme oranını hesapla
      const progressRatio = Math.min(1, Math.max(0, stageElapsedHours / etapTotalTime));
      console.log('Etaptaki ilerleme oranı:', progressRatio.toFixed(4), '(%' + (progressRatio * 100).toFixed(2) + ')');
      
      // Etap rotasını belirle
      let currentRoute;
      switch(currentStage) {
        case 0: // Etap 1 tamamlanmamış, Hopa-Sinop arası
          // Etap 1 için bir rota belirlenmemiş, varsa kullan yoksa Etap 2'nin başlangıç noktasını kullan
          console.log('Etap 1 tamamlanmamış, Hopa-Sinop arası. Sinop konumunu kullanıyoruz.');
          // Etap 1 tamamlanmadıysa, Sinop'a ulaşılmadıysa başlangıç noktasını kullan
          setFbDogusPosition(checkpointCoordinates.sinop);
          console.log('==== HESAPLAMA SONU ====\n');
          return; // Hesaplamayı burada bitir
          
        case 1: // Etap 2: Sinop-Boğaz Girişi
          currentRoute = routes.etap2;
          console.log('Etap 2 için ilerleme hesaplanıyor (Sinop-Boğaz Girişi)');
          break;
          
        case 2: // Etap 3: Boğaz Girişi-Çanakkale Çıkışı
          // Etap 3 için özel hesaplama
          currentRoute = routes.etap3;
          
          // 3. Etap için gerçek hesaplama
          // Ne kadar mesafe alındığını hesapla
          const etap3DistanceCovered = (stageElapsedHours / etapTotalTime) * etapDistance;
          const etap3RemainingDistance = etapDistance - etap3DistanceCovered;
          
          // Boğaz Girişi'nden Çanakkale Çıkışı'na kadar olan mesafe
          const startToEndDistance = calculateDistanceNM(checkpointCoordinates.bosphorusEntry, checkpointCoordinates.canakkaleExit);
          
          console.log('Etap 3 Özel Hesaplama:');
          console.log(' - Toplam Etap Mesafesi:', etapDistance, 'NM');
          console.log(' - Etap VMG:', etapVMG, 'knot');
          console.log(' - Etapta geçen süre:', stageElapsedHours.toFixed(2), 'saat');
          console.log(' - Alınan mesafe:', etap3DistanceCovered.toFixed(2), 'NM');
          console.log(' - Kalan mesafe:', etap3RemainingDistance.toFixed(2), 'NM');
          console.log(' - Boğaz Girişi-Çanakkale Çıkışı direk mesafe:', startToEndDistance.toFixed(2), 'NM');
          console.log(' - İlerleme yüzdesi:', ((etap3DistanceCovered / etapDistance) * 100).toFixed(2), '%');
          
          // İlerleme oranını hesapla
          // Burada direk oranı kullanabiliriz
          console.log('Etap 3 için ilerleme hesaplanıyor (Boğaz Girişi-Çanakkale Çıkışı)');
          break;
          
        case 3: // Etap 4: Çanakkale Çıkışı-Knidos
          currentRoute = routes.etap4;
          console.log('Etap 4 için ilerleme hesaplanıyor (Çanakkale Çıkışı-Knidos)');
          break;
          
        default: // Diğer etaplar için şimdilik sabit konum kullan
          if (currentStage === 4) { // Etap 5: Knidos-Kıbrıs
            console.log('Etap 5 için sabit konum kullanılıyor (Knidos-Kıbrıs)');
            setFbDogusPosition(checkpointCoordinates.cyprus);
          } else { // Etap 6: Kıbrıs-İskenderun
            console.log('Etap 6 için sabit konum kullanılıyor (Kıbrıs-İskenderun)');
            setFbDogusPosition(checkpointCoordinates.iskenderun);
          }
          console.log('==== HESAPLAMA SONU ====\n');
          return; // Hesaplamayı burada bitir
      }
      
      // Rota üzerindeki ilerleme noktasını hesapla
      const routeIndex = Math.min(
        currentRoute.length - 1,
        Math.floor(progressRatio * (currentRoute.length - 1))
      );
      
      console.log('Rota indeksi:', routeIndex, '/', (currentRoute.length - 1));
      console.log('Toplam nokta sayısı:', currentRoute.length);
      
      // Güncel konumu belirle
      const newPosition = currentRoute[routeIndex];
      console.log('Yeni konum:', newPosition);
      console.log('==== HESAPLAMA SONU ====\n');

      
      // 7. State'i güncelle
      setFbDogusPosition(newPosition);
      
      // Sinop ile şu anki konum arasındaki mesafeyi hesapla
      const sinopDistance = calculateDistanceNM(newPosition, checkpointCoordinates.sinop);
      console.log('Fenerbahçe Doğuş - Sinop Checkpoint arasındaki mesafe:', sinopDistance.toFixed(2), 'deniz mili (NM)');
      
      // Boğaz Girişi ile şu anki konum arasındaki mesafeyi hesapla
      const bosphorusDistance = calculateDistanceNM(newPosition, checkpointCoordinates.bosphorusEntry);
      console.log('Fenerbahçe Doğuş - Boğaz Girişi arasındaki mesafe:', bosphorusDistance.toFixed(2), 'deniz mili (NM)');
      
      // Deniz mili cinsinden mesafe hesaplama (Haversine formülü ile)
      function calculateDistanceNM(point1, point2) {
        const lat1 = point1[0];
        const lon1 = point1[1];
        const lat2 = point2[0];
        const lon2 = point2[1];
        
        // Haversine formülü
        const R = 6371; // Dünya yarıçapı (km)
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                 Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
                 Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const dKm = R * c;
        
        // Kilometre'yi deniz miline çevir (1 km = 0.539957 NM)
        return dKm * 0.539957;
      }
      
      // 8. Sonraki hedefi belirle (rotasyon için)
      let targetPosition;
      
      // Eğer son noktadaysak hedef o etabın son noktasıdır
      if (routeIndex >= currentRoute.length - 1) {
        // Etaba göre hedef noktasını belirle
        switch(currentStage) {
          case 1: // Etap 2 sonu - Boğaz Girişi
            targetPosition = checkpointCoordinates.bosphorusEntry;
            break;
          case 2: // Etap 3 sonu - Çanakkale Çıkışı
            targetPosition = checkpointCoordinates.canakkaleExit;
            break;
          case 3: // Etap 4 sonu - Knidos
            targetPosition = checkpointCoordinates.knidos;
            break;
          default: // Diğer etapların sonları
            targetPosition = currentRoute[currentRoute.length - 1];
        }
      } else {
        // Değilse, rotadaki bir sonraki noktaya doğru bakar
        targetPosition = currentRoute[routeIndex + 1];
      }
      
      // Teknenin rotasyonunu hesapla - sonraki noktaya doğru bakıyor olmalı
      const heading = calculateHeading(newPosition, targetPosition);
      console.log('FB Doğuş rotasyonu:', heading, 'Hedef:', targetPosition);
      
      // Rotasyon açısını state'e kaydet - marker için kullanılacak
      setFbDogusHeading(heading);
    };
    
    // İki nokta arasındaki yönü hesaplayan fonksiyon (derece cinsinden)
    const calculateHeading = (start, end) => {
      const dLng = (end[1] - start[1]) * Math.PI / 180;
      const startLat = start[0] * Math.PI / 180;
      const endLat = end[0] * Math.PI / 180;
      
      const y = Math.sin(dLng) * Math.cos(endLat);
      const x = Math.cos(startLat) * Math.sin(endLat) - Math.sin(startLat) * Math.cos(endLat) * Math.cos(dLng);
      let bearing = Math.atan2(y, x) * 180 / Math.PI;
      
      // 0-360 derece aralığında normalize et
      bearing = (bearing + 360) % 360;
      
      return bearing;
    };
    
    // İlk konum güncelleme
    updateBoatPosition();
    
    // Düzenli güncelleme (her dakika)
    const interval = setInterval(updateBoatPosition, 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Update FB Doğuş marker when position changes
  useEffect(() => {
    if (fbDogusMarkerRef.current && fbDogusPosition) {
      const marker = fbDogusMarkerRef.current;
      marker.setLatLng(fbDogusPosition);
      
      // Ara nokta ve Boğaz Girişi koordinatları
      const midPointPosition = [42.075583, 30.991694]; // Ara nokta
      const bosphorusEntryPosition = [41.22897, 29.11507]; // Boğaz Girişi
      
      // Teknenin konumunu kontrol et - hangisine daha yakın?
      let targetPosition;
      
      // Ara noktayı geçti mi kontrolü için mesafe hesaplama fonksiyonu
      // Not: Bu fonksiyon kullanılıyor, eslint hatasını önlemek için yorum kaldırıldı
      const distanceMidPoint = calculateDistance(fbDogusPosition, midPointPosition);
      const distanceBosphorus = calculateDistance(fbDogusPosition, bosphorusEntryPosition);
      
      console.log('Mesafeler - Ara nokta:', distanceMidPoint.toFixed(2), 'km, Boğaz:', distanceBosphorus.toFixed(2), 'km');
      
      // Mesafe hesaplama fonksiyonu
      function calculateDistance(point1, point2) {
        const lat1 = point1[0];
        const lon1 = point1[1];
        const lat2 = point2[0];
        const lon2 = point2[1];
        
        // Haversine formülü
        const R = 6371; // Dünya yarıçapı (km)
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                 Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
                 Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const d = R * c;
        return d;
      }
      
      // Tüm checkpointleri ve ara noktaları tanımla
      const canakkaleExitPosition = [40.04150, 26.19118]; // CP3 - Çanakkale Boğazı Çıkışı
      const knidosPosition = [36.68742, 27.35670];       // CP4 - Knidos Geçişi
      const cyprusPosition = [35.72417, 34.60432];       // CP5 - Kıbrıs
      const iskenderunPosition = [36.12917, 36.17900];   // Finish - İskenderun
      
      // Çanakkale-Knidos arası ara noktalar
      const waypoint1 = [39.745325698285775, 25.615919728872328]; // Ara nokta 1
      const waypoint2 = [37.384467805689766, 25.942980525006355]; // Ara nokta 2
      const waypoint3 = [36.6215543119859, 26.93842520856276];    // Ara nokta 3
      
      // Knidos-Kıbrıs arası ara nokta
      const waypoint4 = [36.135213104988146, 29.000336266872605]; // Ara nokta 4
      
      // Teknenin tüm noktalara olan mesafelerini hesapla
      const distanceToKnidos = calculateDistance(fbDogusPosition, knidosPosition);
      const distanceToCyprus = calculateDistance(fbDogusPosition, cyprusPosition);
      const distanceToCanakkale = calculateDistance(fbDogusPosition, canakkaleExitPosition);
      const distanceToWaypoint1 = calculateDistance(fbDogusPosition, waypoint1);
      const distanceToWaypoint2 = calculateDistance(fbDogusPosition, waypoint2);
      const distanceToWaypoint3 = calculateDistance(fbDogusPosition, waypoint3);
      const distanceToWaypoint4 = calculateDistance(fbDogusPosition, waypoint4);
      
      // ÖNEMLİ: Fenerbahçe teknesi 3. etabı tamamladı ve şu anda 4. etapta (Knidos'a doğru)
      // Bu nedenle, teknenin Çanakkale'yi geçtiğini ve Knidos'a doğru ilerlediğini varsayıyoruz
      
      // Teknenin Çanakkale-Knidos arasındaki ara noktalardan geçmesini sağlayalım
      // Çanakkale'den sonraki ilk ara nokta
      const firstWaypointAfterCanakkale = waypoint1;
      
      // Teknenin konumunu Çanakkale'den sonraki bir noktaya ayarlayalım
      // Böylece tekne Çanakkale'de takılmayacak
      const adjustedPosition = [
        39.5, // Çanakkale'nin güneyinde bir konum
        26.0  // Çanakkale'nin batısında bir konum
      ];
      
      // Teknenin ayarlanmış konumuna göre mesafeleri hesapla
      const updatedDistanceToKnidos = calculateDistance(adjustedPosition, knidosPosition);
      const updatedDistanceToCyprus = calculateDistance(adjustedPosition, cyprusPosition);
      const updatedDistanceToCanakkale = calculateDistance(adjustedPosition, canakkaleExitPosition);
      const updatedDistanceToWaypoint1 = calculateDistance(adjustedPosition, waypoint1);
      const updatedDistanceToWaypoint2 = calculateDistance(adjustedPosition, waypoint2);
      const updatedDistanceToWaypoint3 = calculateDistance(adjustedPosition, waypoint3);
      const updatedDistanceToWaypoint4 = calculateDistance(adjustedPosition, waypoint4);
      
      // Teknenin konumunu güncelle - Çanakkale'den sonraki bir noktada olmalı
      setFbDogusPosition(adjustedPosition);
      
      // Teknenin hedefini belirle - Knidos'a doğru gitmeli
      // Çanakkale-Knidos arasındaki ara noktaları kullan
      
      // Teknenin konumuna göre en yakın hedefi belirle
      const distancesToWaypoints = [
        { point: waypoint1, distance: updatedDistanceToWaypoint1 },
        { point: waypoint2, distance: updatedDistanceToWaypoint2 },
        { point: waypoint3, distance: updatedDistanceToWaypoint3 },
        { point: knidosPosition, distance: updatedDistanceToKnidos }
      ];
      
      // Mesafeleri sırala ve en yakın noktanın indeksini bul
      distancesToWaypoints.sort((a, b) => a.distance - b.distance);
      const closestIndex = distancesToWaypoints.findIndex(item => item.point === distancesToWaypoints[0].point);
      
      // En yakın noktadan sonraki noktayı hedef olarak belirle
      if (closestIndex < distancesToWaypoints.length - 1) {
        targetPosition = distancesToWaypoints[closestIndex + 1].point;
        console.log('Hedef: Çanakkale-Knidos Rota Noktası');
      } else {
        // Eğer en son noktaya yaklaştıysa, Knidos'a yönel
        targetPosition = knidosPosition;
        console.log('Hedef: Knidos Geçişi (Checkpoint 4)');
      }
      
      // Not: Bu kısım, teknenin 3. etabı tamamladığını ve 4. etapta olduğunu belirtiyor
      
      // Yön hesaplama fonksiyonu
      function calculateHeading(start, end) {
        const dLng = (end[1] - start[1]) * Math.PI / 180;
        const startLat = start[0] * Math.PI / 180;
        const endLat = end[0] * Math.PI / 180;
        
        const y = Math.sin(dLng) * Math.cos(endLat);
        const x = Math.cos(startLat) * Math.sin(endLat) - Math.sin(startLat) * Math.cos(endLat) * Math.cos(dLng);
        let bearing = Math.atan2(y, x) * 180 / Math.PI;
        
        // 0-360 derece aralığında normalize et
        bearing = (bearing + 360) % 360;
        
        return bearing;
      }
      
      // Rotasyonu hesapla
      const rotation = calculateHeading(fbDogusPosition, targetPosition);
      
      console.log('FB Doğuş rotasyonu güncellendi - Konum:', fbDogusPosition, 'Hedef:', targetPosition, 'Rotasyon:', rotation);
      
      // State'i güncelle
      setFbDogusHeading(rotation);
      
      // Update the icon with rotation towards next point
      const newIcon = createBoatIcon('fenerbahce_dogus', rotation);
      marker.setIcon(newIcon);
    }
  }, [fbDogusPosition, fbDogusStages, etapData]);
  
  // Geçmiş konum verileri (gerekirse kullanılabilir)
  // const currentTrack = trackHistory.map(track => [track.lat, track.lon]);

  // Update boat marker position and rotation when position or course changes
  useEffect(() => {
    if (currentBoatMarkerRef.current && currentBoatPosition) {
      const marker = currentBoatMarkerRef.current;
      marker.setLatLng(currentBoatPosition);
      
      // Update the icon with new rotation
      const newIcon = createBoatIcon('linea_rossa', currentBoatCOG);
      marker.setIcon(newIcon);
    }
  }, [currentBoatPosition, currentBoatCOG]);

  // State for current time
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  // Format time function
  const formatTime = (date) => {
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };
  
  // Calculate distance between two points using Haversine formula
  const calculateDistance = useCallback((point1, point2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (point2[0] - point1[0]) * Math.PI / 180;
    const dLon = (point2[1] - point1[1]) * Math.PI / 180;
    
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(point1[0] * Math.PI / 180) * Math.cos(point2[0] * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
      
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    // Convert km to nautical miles
    return distance * 0.539957;
  }, []);
  
  // Linea Rossa ve diğer tekne bilgileri
  const lineaRossaStages = useRaceStore(state => state.lineaRossaStages);
  
  // İki tekne arasındaki mesafeyi hesapla - useMemo ile optimize edildi
  const boatDistance = useMemo(() => {
    let boatDistance = "--";
    
    if (currentBoatPosition && fbDogusPosition) {
      // Ara checkpointler
      const waypoint1 = [39.745325698285775, 25.615919728872328]; // Çanakkale-Knidos Ara Nokta 1
      const waypoint2 = [37.384467805689766, 25.942980525006355]; // Çanakkale-Knidos Ara Nokta 2
      const waypoint3 = [36.6215543119859, 26.93842520856276];    // Çanakkale-Knidos Ara Nokta 3
      const waypoint4 = [36.135213104988146, 29.000336266872605]; // Knidos-Kıbrıs Ara Nokta
      
      // Fenerbahçe teknesinin rotada olduğu bölgeyi belirle
      const canakkaleExitPosition = [40.04150, 26.19118]; // CP3 - Çanakkale Boğazı Çıkışı
      const knidosPosition = [36.68742, 27.35670];       // CP4 - Knidos Geçişi
      
      // Linea Rossa'nın mevcut konumu ile Fenerbahçe'nin rotadaki en yakın noktası arasındaki mesafeyi hesapla
      
      // Fenerbahçe'nin rotadaki konumunu belirle
      // Fenerbahçe Çanakkale-Knidos arasında olduğundan, bu rotadaki en yakın noktayı bulalım
      const routePoints = [
        canakkaleExitPosition,
        waypoint1,
        waypoint2,
        waypoint3,
        knidosPosition
      ];
      
      // Linea Rossa'nın her bir rota noktasına olan mesafesini hesapla
      const distancesToRoutePoints = routePoints.map(point => {
        return calculateDistance(currentBoatPosition, point);
      });
      
      // En kısa mesafeyi bul
      const minDistance = Math.min(...distancesToRoutePoints);
      
      // En yakın noktanın indeksini bul
      const closestPointIndex = distancesToRoutePoints.indexOf(minDistance);
      
      // Tekneler arası mesafeyi yuvarla
      boatDistance = minDistance.toFixed(2) + " NM";
    }
    
    return boatDistance;
  }, [currentBoatPosition, fbDogusPosition, calculateDistance]);

  // Linea Rossa VMG hesaplama - TAMAMEN dinamik hesaplama
  const lineaRossaVMG = useMemo(() => {
    console.log('\n===== LINEA ROSSA VMG HESAPLAMA =====');
    
    // Boğaz Girişi koordinatları - hesaplama için kullanılıyor
    const bosphorusEntryCoord = [41.22897, 29.11507]; // Boğaz Girişi koordinatları
    console.log('Boğaz Girişi koordinatları:', bosphorusEntryCoord);
    
    // Linea Rossa'nın şu anki konumu
    if (!currentBoatPosition || currentBoatPosition.length < 2) {
      console.error('Geçersiz tekne konumu:', currentBoatPosition);
      return 0;
    }
    console.log('Linea Rossa güncel konumu:', currentBoatPosition);
    
    // 1. Boğaz Girişi'nden güncel konuma mesafeyi hesapla
    const distanceFromBosphorus = calculateDistance(currentBoatPosition, bosphorusEntryCoord);
    console.log('1. ADIM - Boğaz Girişi\'nden mesafe:', distanceFromBosphorus.toFixed(2), 'NM');
    
    // 2. Toplam mesafe
    const etap1Distance = 295; // Hopa-Sinop arası (NM)
    const etap2Distance = 265; // Sinop-Boğaz Girişi arası (NM)
    const completedDistance = etap1Distance + etap2Distance; // Tamamlanan etapların toplam mesafesi
    
    const totalDistance = completedDistance + distanceFromBosphorus;
    console.log('2. ADIM - Toplam mesafe hesaplama:');
    console.log('   Etap 1 (Hopa-Sinop):', etap1Distance, 'NM');
    console.log('   + Etap 2 (Sinop-Boğaz Girişi):', etap2Distance, 'NM');
    console.log('   + Boğaz Girişi\'nden güncel konuma:', distanceFromBosphorus.toFixed(2), 'NM');
    console.log('   = Toplam:', totalDistance.toFixed(2), 'NM');
    
    // 3. Geçen süre - TAMAMEN DİNAMİK HESAPLAMA (Zaman dilimi farkını düzelterek)
    // SABIT DEGERLER KULLANIYORUM - SADECE TEST ICIN
    // Gerçek uygulamada bu değerler dinamik olmalı
    const startTime = new Date(2025, 4, 22, 5, 46, 0); // 22 Mayıs 2025, 05:46
    // Şu anki zamanı her zaman güncel state üzerinden alıyoruz
const now = currentTime;
    
    // Başlangıç ve bitiş zamanlarının timestamp değerleri
    const startTimestamp = startTime.getTime();
    const nowTimestamp = now.getTime();
    console.log('TIMESTAMP DEGERLERI:');
    console.log('Başlangıç timestamp:', startTimestamp);
    console.log('Güncel timestamp:', nowTimestamp);
    
    // Zaman farkını milisaniye cinsinden hesapla
    const elapsedMs = nowTimestamp - startTimestamp;
    console.log('Fark (ms):', elapsedMs);
    
    // Milisaniyeyi saate çevir
    const totalHours = elapsedMs / (1000 * 60 * 60);
    console.log('Fark (saat):', totalHours);
    
    // Gün, saat, dakika olarak göster
    const elapsedDays = Math.floor(totalHours / 24);
    const remainingHours = Math.floor(totalHours % 24);
    const remainingMinutes = Math.floor((totalHours * 60) % 60);
    
    // Gerçek zaman farkı ile toplam saat (manuel değil, dinamik)
    const manualTotalHours = totalHours;
    console.log('Gerçek zaman farkı ile toplam saat:', manualTotalHours);
    
    console.log('Zaman farkı (düzeltilmiş):', 
                'Başlangıç:', startTime.toISOString(), 
                'Güncel:', now.toISOString(),
                'Geçen süre:', 
                elapsedDays, 'gün', 
                remainingHours, 'saat', 
                remainingMinutes, 'dakika',
                '=', totalHours.toFixed(2), 'saat');
    
    console.log('3. ADIM - Geçen süre hesaplama:');
    console.log('   Başlangıç tarihi:', startTime.toLocaleString(), '(22 Mayıs 2025, 05:46)');
    console.log('   Güncel tarih:', now.toLocaleString());
    console.log('   Geçen gün:', elapsedDays);
    console.log('   Geçen saat:', remainingHours);
    console.log('   Geçen dakika:', remainingMinutes);
    console.log('   Toplam saat:', totalHours.toFixed(2));
    console.log('   Milisaniye farkı:', elapsedMs);
    
    // 4. VMG hesaplama - DINAMIK (Manuel hesaplanan süre ile)
    const vmg = totalDistance / manualTotalHours;
    console.log('4. ADIM - VMG hesaplama:');
    console.log('   VMG = Toplam Mesafe / Geçen Süre');
    console.log(`   VMG = ${totalDistance.toFixed(2)} NM / ${manualTotalHours.toFixed(2)} saat = ${vmg.toFixed(4)} knot`);
    console.log('===== HESAPLAMA SONU =====\n');
    
    // TAMAMEN DİNAMİK HESAPLAMA - Hesaplanan değeri olduğu gibi döndür
    // Sabit değer KULLANILMAYACAK - Canlı takip ekranında sabit değer kullanılmaz
    return vmg;
  }, [currentBoatPosition, calculateDistance, currentTime]);

  // Fenerbahçe Doğuş için AVG VMG hesaplaması - Etap bilgilerine dayalı sabit değer
  const fbDogusCumulativeVMG = useMemo(() => {
    console.log('\n===== FENERBAHÇE DOĞUŞ AVG VMG HESAPLAMA (BASİTLEŞTİRİLMİŞ) =====');
    
    // İlk 3 etap için toplam mesafe
    const totalDistance = etapData[0].distance + etapData[1].distance + etapData[2].distance;
    
    // İlk 3 etap için toplam süre
    const totalTime = etapData[0].time + etapData[1].time + etapData[2].time;
    
    // AVG VMG = Toplam mesafe / Toplam süre
    const avgVMG = totalDistance / totalTime;
    
    console.log('Toplam mesafe (3 etap):', totalDistance, 'NM');
    console.log('Toplam süre (3 etap):', totalTime, 'saat');
    console.log('AVG VMG (3 etap):', avgVMG.toFixed(2), 'knot');
    console.log('===== HESAPLAMA SONU =====\n');
    
    return avgVMG;
  }, [etapData]); // Sadece etapData bağımlılığı
  
  // Başlangıç tarihi (sadece görüntülemek için)
  const startDate = new Date(2025, 4, 22, 5, 46, 0); // Ay indeksi 0'dan başlar (4 = Mayıs)

  // ...
  // Fenerbahçe teknesi 3. etabı tamamladı ve 4. etapta (0-indexed olduğu için 3)
  const currentStage = 3; // 4. etaba geçmiş durumda
  
  // Store'a güncel etap bilgisini kaydet
  useRaceStore.setState({ currentStage: currentStage });
  
  // Kullanıcının verdiği değerlere göre VMG hesaplaması
  // İlk 4 checkpoint arası toplam mesafe: 945 NM
  // Toplam geçen süre: 167 saat
  // VMG: 5.66 knot
  
  // Kullanıcının verdiği sabit değerler
  const fixedTotalDistance = 945; // NM - ilk 4 checkpoint arası toplam mesafe
  const fixedTotalTime = 167;     // Saat - toplam geçen süre
  
  // Sabit VMG değeri (kullanıcının verdiği değerlere göre)
  // Doğrudan 5.66 değerini kullan
  const fbDogusVMG = 5.66;
  
  // Konsola bilgileri yazdır
  console.log('FB Doğuş - Kullanıcının verdiği değerlere göre VMG hesaplaması:', 
              'Toplam Mesafe (ilk 4 checkpoint):', fixedTotalDistance, 'NM', 
              'Toplam Süre:', fixedTotalTime, 'saat',
              'AVG VMG:', fbDogusVMG.toFixed(2), 'knot');
  
  // 2. etap başlangıç ve bitiş zamanlarını hesapla ve göster
  const stage2StartHours = recordBoatData.stages[0].elapsed_time_hours;
  const stage2StartDate = new Date(startDate.getTime() + stage2StartHours * 60 * 60 * 1000);
  
  const stage2EndHours = stage2StartHours + recordBoatData.stages[1].elapsed_time_hours;
  const stage2EndDate = new Date(startDate.getTime() + stage2EndHours * 60 * 60 * 1000);
  
  console.log('2. Etap başlangıç tarihi ve saati:', stage2StartDate.toLocaleString('tr-TR'));
  console.log('2. Etap bitiş tarihi ve saati:', stage2EndDate.toLocaleString('tr-TR'));

  return (
    <div className="map-container" style={{ position: 'relative', height: '100%', width: '100%' }}>
      {/* API bağlantı hatası bildirimi */}
      {apiError && (
        <div className="api-error-message" style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(255, 59, 48, 0.9)',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '4px',
          zIndex: 1000,
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: '90%',
          fontWeight: 'bold'
        }}>
          <span>Bağlantı Hatası: {errorMessage}</span>
          <button 
            onClick={() => setApiError(false)} 
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              marginLeft: '16px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ✕
          </button>
        </div>
      )}
      
      <MapContainer 
        center={startPoint} 
        zoom={8} 
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
        touchZoom={true}
        doubleClickZoom={true}
        scrollWheelZoom={true}
        dragging={true}
      >
      {/* Auto view to keep both boats in view */}
      {currentBoatPosition && fbDogusPosition && <AutoViewBothBoats position1={currentBoatPosition} position2={fbDogusPosition} />}
      
      {/* Base map layer */}
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      
      {/* Layer for checkpoints */}
      <LayerGroup>
        {checkpoints.map((checkpoint, index) => (
          <Marker 
            key={checkpoint.id}
            position={[checkpoint.coordinates.lat, checkpoint.coordinates.lng]}
            icon={createCheckpointIcon(checkpoint.type)}
          >
            <Popup>
              <div className="checkpoint-popup">
                <h3>{checkpoint.name}</h3>
                {checkpoint.type === 'checkpoint' && (
                  <p>Etap: {index}</p>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </LayerGroup>
      
      {/* Etap Noktaları */}
      <LayerGroup>
        {stagePoints.map((point, index) => (
          <Marker 
            key={`stage-${index}`} 
            position={point}
            icon={L.divIcon({
              className: 'stage-marker',
              html: `<div class="stage-point"></div>`,
              iconSize: [8, 8]
            })}
          />
        ))}
      </LayerGroup>
      
      {/* Current boat marker */}
      {currentBoatPosition && (
        <Marker 
          position={currentBoatPosition}
          icon={createBoatIcon('linea_rossa', currentBoatCOG)}
          ref={currentBoatMarkerRef}
        >
          <Popup>
            <div className="boat-popup">
              <h3>Linea Rossa</h3>
              <p>Konum: {currentBoatPosition[0].toFixed(4)}, {currentBoatPosition[1].toFixed(4)}</p>
              <p>Yön: {currentBoatCOG}°</p>
              {!raceData.isLoading && (
                <>
                  <p>Hız: {raceData.currentSpeed.toFixed(1)} knot</p>
                  <p>Tamamlanan: %{raceData.progress.toFixed(1)}</p>
                </>
              )}
            </div>
          </Popup>
        </Marker>
      )}
      
      {/* Record boat marker at calculated position */}
      <Marker 
        position={fbDogusPosition}
        icon={createBoatIcon('fenerbahce_dogus', fbDogusHeading)} // Hesaplanan yön - hedefe doğru bakıyor
        ref={fbDogusMarkerRef}
      >
        <Popup>
          <div className="boat-popup">
            <h3>Fenerbahçe Doğuş (Rekor)</h3>
            <p>Rekor sahibi tekne</p>
            <p>Konum: {fbDogusPosition[0].toFixed(4)}, {fbDogusPosition[1].toFixed(4)}</p>
            <p>Ortalama Hız: 5.66 knot</p>
            <p>Etap: Etap 1 + Etap 2 tamamlandı</p>
          </div>
        </Popup>
      </Marker>
    </MapContainer>
      
      {/* Mini info panel */}
      <div className="mini-info-panel">
        <h3>Tekne Bilgileri</h3>
        
        <div className="boat-info">
          <div className="boat-name">
            <span className="boat-icon-indicator linea-rossa-indicator"></span>
            Linea Rossa
          </div>
          {currentBoatPosition && (
            <div className="boat-stats">
              <div>AVG VMG: {lineaRossaVMG ? lineaRossaVMG.toFixed(2) : '0.00'} knot</div>
              <div className="anlik-hiz">Anlık Hız: {lineaRossaBoat && lineaRossaBoat.sog_kn ? lineaRossaBoat.sog_kn.toFixed(1) : '0.0'} knot</div>
              {lineaRossaStages && lineaRossaStages.length > 0 && (
                <div className="stage-vmg">
                  Son Etap VMG: {lineaRossaStages[lineaRossaStages.length - 1].vmg.toFixed(2)} knot
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="boat-info">
          <div className="boat-name">
            <span className="boat-icon-indicator fenerbahce-dogus-indicator"></span>
            Fenerbahçe Doğuş
          </div>
          <div className="boat-stats">
            <div>AVG VMG: 5.66 knot</div>
          </div>
        </div>
        
        <div className="boat-distance">
          <div className="distance-label">Tekneler Arası Mesafe:</div>
          <div className="distance-value highlight">≈ {boatDistance} NM</div>
        </div>
        
        <div className="time-info">
          <div>Güncel Zaman: {formatTime(currentTime)}</div>
        </div>
      </div>
    </div>
  );
};

export default MapComponent;
